import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useEffect, useState } from "react";
import eventService from "../../services/eventService";
import {toast} from "react-toastify";

const Home = () => {
  const { user } = useAuth();

    const fallbackImages = [
        "https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=800",
        "https://images.unsplash.com/photo-1546519638-68e109498ffc?w=800",
        "https://images.unsplash.com/photo-1505373877841-8d25f7d46678?w=800",
    ];

    const [events, setEvents] = useState([]);

    const slides = React.useMemo(() => {
        // ✅ If no events → use fallback images
        if (!events || events.length === 0) {
            return fallbackImages.map(img => ({
                type: 'image',
                src: img,
            }));
        }

        // ✅ Map events to slides
        return events.map(event => {
            if (event.video_link) {
                return {
                    type: 'video',
                    src: event?.video_link
                        ? `${process.env.REACT_APP_URL}/storage/${event.video_link}`
                        : null
                };
            }

            if (event.images?.[0]?.image_path) {
                return {
                    type: 'image',
                    src: `${process.env.REACT_APP_URL}/storage/${event.images[0].image_path}`,
                };
            }

            // ✅ Fallback per event
            return {
                type: 'image',
                src: fallbackImages[0],
            };
        });
    }, [events]);

    const PER_PAGE = 5;

    const fetchEvents = async (page = 1) => {
        try {


            const params = {
                page,
                per_page: PER_PAGE,
            };


            const response = await eventService.getEvents(params);

            // ✅ Laravel paginator structure
            const paginated = response.data.data;

            setEvents(paginated.data);

        } catch (error) {
            console.error('Error fetching events:', error);
            toast.error('Failed to load events');
            setEvents([]);
        } finally {

        }
    };

    useEffect(() => {
        fetchEvents(1);
    }, []);

    const [index, setIndex] = useState(0);

    useEffect(() => {
        const interval = setInterval(nextSlide, 5000);
        return () => clearInterval(interval);
    }, [index]);

    const nextSlide = () => {
        setIndex((prev) =>
            slides.length ? (prev + 1) % slides.length : 0
        );
    };

    const prevSlide = () => {
        setIndex((prev) =>
            slides.length ? (prev - 1 + slides.length) % slides.length : 0
        );
    };

    const industries = [
        "Venues, Theaters & Arenas",
        "Concerts & Concert Tours",
        "Performing Arts",
        "Sports",
        "Schools",
        "Festivals",
        "Rodeo",
        "Ballrooms",
        "Recurring Events",
        "Amusement Parks",
        "Nonprofit & Charity",
        "Galas & Fundraising",
        "Tours & Transportation"
    ];

  return (
    <div className="min-h-screen">
        <section className="relative h-screen text-white overflow-hidden">

            {/* Background Carousel */}
            <div className="absolute inset-0">
                {slides.map((slide, i) => (
                    <div
                        key={i}
                        className={`absolute inset-0 transition-opacity duration-1000 ${
                            i === index ? 'opacity-100 z-10' : 'opacity-0 z-0'
                        }`}
                    >
                        {slide.type === 'video' ? (
                            <video
                                src={slide.src}
                                autoPlay
                                muted
                                loop
                                playsInline
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <div
                                className="w-full h-full bg-cover bg-center"
                                style={{
                                    backgroundImage: `url(${slide.src})`,
                                }}
                            />
                        )}
                        <div className="absolute inset-0 bg-black/50" />
                    </div>
                ))}
            </div>

            {/* Dark Overlay */}
            <div className="absolute inset-0 bg-black/50"></div>

            <div className="absolute inset-0 z-10 flex items-center justify-between px-6">
                <button
                    onClick={prevSlide}
                    className="bg-black/40 hover:bg-black/60 text-white w-12 h-12 rounded-full flex items-center justify-center transition"
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                </button>

                <button
                    onClick={nextSlide}
                    className="bg-black/40 hover:bg-black/60 text-white w-12 h-12 rounded-full flex items-center justify-center transition"
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            </div>

            {/* Decorative blobs */}
            <div className="absolute inset-0">
                <div className="absolute top-20 left-10 w-72 h-72 bg-yellow-600 rounded-full blur-xl opacity-30 animate-blob"></div>
                <div className="absolute top-40 right-10 w-72 h-72 bg-yellow-600 rounded-full blur-xl opacity-30 animate-blob animation-delay-2000"></div>
                <div className="absolute bottom-20 left-1/2 w-72 h-72 bg-yellow-600 rounded-full blur-xl opacity-30 animate-blob animation-delay-4000"></div>
            </div>

            {/* CONTENT */}
            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
                <div className="text-center">

                    <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold mb-6">
                        Discover Amazing
                        <span className="block text-yellow-300">Events Near You</span>
                    </h1>

                    <p className="max-w-2xl mx-auto text-xl sm:text-2xl mb-10 text-gray-100">
                        Buy tickets instantly, get digital passes, and never miss out on the experiences you love
                    </p>

                    {/* CTA Buttons */}
                    <div className="flex flex-col sm:flex-row gap-8 justify-center items-center">
                        <Link to="/events" className=" nav-link border-2 border-yellow-400 px-8 py-3 text-lg bg-yellow-400 text-white hover:from-yellow-500 hover:to-orange-600 shadow-2xl transform hover:scale-105 transition-all duration-200">
                          <span className="flex items-center">
                            <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            Explore Events
                          </span>
                        </Link>

                        {!user && (
                            <Link to="/register" className=" nav-link px-8 py-3 text-lg border-2 border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-white shadow-lg">
                                <span className="flex items-center">
                                  <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                                  </svg>
                                  Sign Up Free
                                </span>
                            </Link>
                        )}
                    </div>
                    <div className="mt-22 grid grid-cols-2 md:grid-cols-4 gap-8">
                        {[
                          { label: 'Active Events', value: '1,200+' },
                          { label: 'Happy Users', value: '50K+' },
                          { label: 'Tickets Sold', value: '200K+' },
                          { label: 'Organizers', value: '500+' }
                        ].map((stat, index) => (
                          <div key={index} className="text-center">
                            <div className="text-3xl sm:text-4xl font-bold text-yellow-300 mb-2">{stat.value}</div>
                            <div className="text-sm sm:text-base text-gray-200">{stat.label}</div>
                          </div>
                        ))}
                      </div>
                </div>
            </div>
        </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-extrabold text-gray-900 mb-4">
              Why Choose <span className="text-yellow-400">Ticketaty</span>
            </h2>
            <p className="text-xl text-gray-600"> Enjoy a seamless experience from browsing events to securing your tickets, all in one platform. Join thousands of satisfied users who trust Ticketaty to make every event experience effortless and unforgettable.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="card card-hover p-8 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-400 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Lightning Fast</h3>
              <p className="text-gray-600">
                Book your tickets in seconds with our streamlined checkout process. No hassle, no delays.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="card card-hover p-8 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-400 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Digital Tickets</h3>
              <p className="text-gray-600">
                Get instant QR code tickets on your phone. Eco-friendly and always accessible.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="card card-hover p-8 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-400 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Secure Payment</h3>
              <p className="text-gray-600">
                Your transactions are protected with bank-level security and encryption.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-extrabold text-yellow-400 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-white">Get your tickets in just 3 simple steps</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="relative">
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-400 rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl">
                  <span className="text-3xl font-bold text-white">1</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Find Events</h3>
                <p className="text-gray-400">
                  Browse through thousands of events across various categories and locations
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative">
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-400 rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl">
                  <span className="text-3xl font-bold text-white">2</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Select Tickets</h3>
                <p className="text-gray-400">
                  Choose your preferred ticket type and quantity, then proceed to secure checkout
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative">
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-400 rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl">
                  <span className="text-3xl font-bold text-white">3</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Enjoy Event</h3>
                <p className="text-gray-400">
                  Show your QR code at the entrance and enjoy an unforgettable experience
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl text-gray-900 font-extrabold mb-6">
              Industries That Trust <span className="text-yellow-400">Ticketaty</span>
          </h2>
          <p className="text-xl mb-10 text-gray-900">
              Discover why leading industries trust <span className="text-yellow-400 font-extrabold">Ticketaty</span> to power their events. From large-scale venues and festivals to schools, nonprofits, and tours, <span className="text-yellow-400 font-extrabold">Ticketaty</span> delivers a secure, flexible, and real-time ticketing platform designed to handle events of every size making every experience smooth, reliable, and stress-free.
          </p>

            {/* Industries / Categories */}
            <div className="mt-14">

                <div className="flex flex-wrap justify-center gap-4">
                    {industries.map((item, index) => (
                        <div
                            key={index}
                            className="group px-5 py-3 backdrop-blur-md bg-white/70 border border-yellow-200
                   rounded-full text-gray-900 text-sm font-medium shadow-md
                   hover:bg-yellow-400 hover:text-white hover:shadow-yellow-400/40
                   hover:scale-105 transition-all duration-300 cursor-default"
                        >
                            {item}
                        </div>
                    ))}
                </div>
            </div>

          <div className="flex flex-col mt-12 sm:flex-row gap-4 justify-center">
            <Link to="/register" className="nav-link border-2 border-yellow-400 px-8 py-3 text-lg bg-yellow-400 text-white hover:from-yellow-500 hover:to-orange-600 shadow-2xl transform hover:scale-105 transition-all duration-200">
              <span className="flex items-center justify-center">
                <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
                Get Started Free
              </span>
            </Link>
            <Link to="/events" className="nav-link bg-gray-100 px-8 py-3 text-lg border-2 border-yellow-400 text-yellow-400 hover:!bg-yellow-400 hover:!text-white shadow-lg">
              Browse Events
            </Link>
          </div>
        </div>
      </section>

      {/* Footer with Portal Links */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <p className="text-gray-400 text-sm">© 2025 Ticketaty. All rights reserved.</p>
            </div>
            <div className="flex space-x-6">
              <Link to="/admin/login" className="text-sm text-white hover:!text-yellow-400 transition-colors flex items-center">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                Admin Portal
              </Link>
              <Link to="/organizer/login" className="text-sm text-white hover:!text-yellow-400 transition-colors flex items-center">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Organizer Portal
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;

